import type { RecipeData } from '@thottbot/shared';
import { FieldTable, Prose, RefLink, refList } from './FieldTable';

const TAUGHT: Record<NonNullable<RecipeData['taught']>, string> = {
  trainer: 'Trainer',
  item: 'Recipe item',
  both: 'Trainer or recipe item',
};

function effectText(e: NonNullable<RecipeData['enchant']>['effects'][number]): string {
  if (e.value === undefined) return e.name;
  return `${e.value > 0 ? '+' : ''}${e.value} ${e.name}`;
}

export function RecipeFields({ data }: { data: RecipeData }) {
  const enchant = data.enchant;
  const resultItem = data.resultItem as (NonNullable<RecipeData['resultItem']> & { slug?: string }) | undefined;
  const recipeItem = data.recipeItem as (NonNullable<RecipeData['recipeItem']> & { slug?: string }) | undefined;
  return (
    <FieldTable
      rows={[
        { label: 'Profession', value: data.profession },
        { label: 'Requires skill', value: data.requiredSkill },
        {
          label: 'Skill colours',
          value:
            data.skillColors &&
            `yellow at ${data.skillColors.yellow}, green at ${data.skillColors.green}, grey at ${data.skillColors.grey}`,
        },
        {
          label: 'Taught by',
          value: data.taught ? (
            <>
              {TAUGHT[data.taught]}
              {recipeItem && (
                <>
                  {' '}
                  (<RefLink r={recipeItem} fallbackType="item" />)
                </>
              )}
            </>
          ) : undefined,
        },
        {
          label: 'Creates',
          value: resultItem ? <RefLink r={resultItem} fallbackType="item" /> : data.resultItemName,
        },
        { label: 'Enchants', value: enchant?.slots.join(', ') },
        {
          label: 'Effect',
          value:
            enchant &&
            [enchant.text, ...enchant.effects.map(effectText).filter((t) => t !== enchant.text)].join(' · ') +
              (enchant.temporary ? ' (temporary)' : ''),
        },
        { label: 'Weapon types', value: enchant?.weaponTypes?.join(', ') },
        {
          label: 'Reagents',
          value: data.reagents?.some((r) => 'artifactId' in r || 'gameId' in r)
            ? refList(
                data.reagents.map((r) => ({ ...r, name: `${r.count ?? 1} x ${r.name}` })),
                'item',
              )
            : data.reagents?.map((r) => `${r.count ?? 1} x ${r.name}`).join(', '),
        },
        { label: 'Source', value: data.source },
        { label: 'Description', value: <Prose text={data.description} /> },
      ]}
    />
  );
}
